import * as React from "react";
import { styled } from "@mui/material/styles";
import { useMediaQuery, useTheme } from "@mui/material";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingIcon from "@mui/icons-material/Settings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddchartOutlined,
  AddCircle,
  ApprovalSharp,
  ArticleOutlined,
  CreateRounded,
  CalendarMonthOutlined,
  RequestQuoteOutlined,
  ViewListOutlined,
} from "@mui/icons-material";
import { useCompany } from "../hooks/useCompany";
import { CompanyStatus } from "../types/company";
import { useUILabel } from "../hooks/useUILabel";

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})<{ drawerOpen?: boolean; }>(({ theme, drawerOpen }) => ({
  borderRadius: 10,
  margin: "4px 8px",
  padding: "10px 16px",
  minHeight: 44,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  justifyContent: drawerOpen ? "flex-start" : "center",
  [theme.breakpoints.down("sm")]: {
    margin: "2px 4px",
    padding: "8px 12px",
    minHeight: 40,
    borderRadius: 8,
  },
  ...(!drawerOpen && {
    margin: "4px",
    padding: "8px",
    minWidth: "auto",
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      margin: 0,
      justifyContent: "center",
    },
  }),
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.04)",
    transform: drawerOpen ? "translateX(4px)" : "none",
    [theme.breakpoints.down("sm")]: {
      transform: drawerOpen ? "translateX(2px)" : "none",
    },
  },
  "&.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(25, 118, 210, 0.16)"
        : "rgba(25, 118, 210, 0.08)",
    color: theme.palette.primary.main,
    fontWeight: 600,
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(25, 118, 210, 0.24)"
          : "rgba(25, 118, 210, 0.12)",
    },
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledNestedListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})<{ drawerOpen?: boolean; }>(({ theme, drawerOpen }) => ({
  borderRadius: 8,
  margin: "2px 8px 2px 24px",
  padding: "8px 16px",
  minHeight: 40,
  position: "relative",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.down("sm")]: {
    margin: "2px 4px 2px 16px",
    padding: "6px 10px",
    minHeight: 36,
    borderRadius: 6,
  },
  ...(!drawerOpen && {
    margin: "2px 4px",
    padding: "8px",
    justifyContent: "center",
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      margin: 0,
      justifyContent: "center",
    },
    "&::before": {
      display: "none",
    },
  }),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.3)"
      : "rgba(0, 0, 0, 0.3)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    [theme.breakpoints.down("sm")]: {
      left: 4,
      width: "3px",
      height: "3px",
    },
  },
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(0, 0, 0, 0.03)",
    transform: "translateX(3px)",
    [theme.breakpoints.down("sm")]: {
      transform: "translateX(2px)",
    },
    "&::before": {
      backgroundColor: theme.palette.primary.main,
      width: "6px",
      height: "6px",
      [theme.breakpoints.down("sm")]: {
        width: "5px",
        height: "5px",
      },
    },
  },
  "&.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(25, 118, 210, 0.12)"
        : "rgba(25, 118, 210, 0.06)",
    color: theme.palette.primary.main,
    fontWeight: 600,
    "&::before": {
      backgroundColor: theme.palette.primary.main,
      width: "6px",
      height: "6px",
      [theme.breakpoints.down("sm")]: {
        width: "5px",
        height: "5px",
      },
    },
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(25, 118, 210, 0.18)"
          : "rgba(25, 118, 210, 0.1)",
    },
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})<{ drawerOpen?: boolean; }>(({ theme, drawerOpen }) => ({
  minWidth: drawerOpen ? 40 : "auto",
  color: "inherit",
  justifyContent: drawerOpen ? "flex-start" : "center",
  "& svg": {
    fontSize: "1.25rem",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: drawerOpen ? 32 : "auto",
    "& svg": {
      fontSize: "1.1rem",
    },
  },
}));

const StyledCollapseList = styled(List)(({ theme }) => ({
  backgroundColor: "transparent",
  padding: "4px 0",
}));

interface SidebarMenuProps {
  open?: boolean;
  onClose?: () => void;
}

const SidebarMenu = ({ open = true, onClose }: SidebarMenuProps) => {
  const navigate = useNavigate();
  const [jobExpanded, setJobExpanded] = React.useState(false);
  const [talentExpanded, setTalentExpanded] = React.useState(false);
  const [reportExpanded, setReportExpanded] = React.useState(false);
  const [settingsExpanded, setSettingsExpanded] = React.useState(false);
  const company = useCompany();
  
  // Sidebar menu labels
  const dashboardLabel = useUILabel("common.dashboard");
  const workMenuLabel = useUILabel("common.workMenu");
  const createNewWorkLabel = useUILabel("common.createNewWork");
  const workCalendarLabel = useUILabel("common.workCalendar");
  const workManagementLabel = useUILabel("common.workManagement");
  const locationsLabel = useUILabel("common.locations");
  const templatesLabel = useUILabel("common.templates");
  const independentProfessionalsLabel = useUILabel("common.independentProfessionals");
  const showListLabel = useUILabel("common.showList");
  const showMapLabel = useUILabel("common.showMap");
  const favoritesLabel = useUILabel("common.favorites");
  const financesLabel = useUILabel("common.finances");
  const generalFinancesLabel = useUILabel("common.generalFinances");
  const financialReportLabel = useUILabel("common.financialReport");
  const settingsLabel = useUILabel("common.settings");
  const addManagersLabel = useUILabel("common.addManagers");
  const uiWordingLabel = useUILabel("common.uiWording");

  const { pathname } = useLocation();
  const page = React.useMemo(() => {
    const parts = pathname.split("/");
    let page = "?";
    if (parts.length >= 3) page = parts[1] + "/" + parts[2];
    else if (parts.length >= 2) page = parts[1];
    return page;
  }, [pathname]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleNavigate = (path: string) => {
    if (isMobile && onClose) {
      onClose();
    }
    navigate(path);
  };

  return (
    <List
      component="nav"
      sx={{
        px: open ? { xs: 0.5, sm: 1 } : 0,
        py: { xs: 1, sm: 2 },
        // Add margin top when drawer is closed to account for navbar/toolbar
        mt: !open ? { xs: "60px", sm: "70px", md: "80px" } : { xs: 0, sm: 0 },
        // Add padding top for mobile when drawer is open to account for Toolbar
        ...(open && isMobile && {
          pt: { xs: 2, sm: 0 },
        }),
      }}
    >
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => handleNavigate("/dashboard")}
        selected={page == "dashboard"}
        sx={{
          ...(open && isMobile && {
            mt: { xs: 1, sm: 0 },
          }),
        }}
      >
        <StyledListItemIcon drawerOpen={open}>
          <DashboardIcon />
        </StyledListItemIcon>
        <ListItemText
          primary={dashboardLabel}
          primaryTypographyProps={{
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
            fontWeight: page == "dashboard" ? 600 : 400,
          }}
        />
      </StyledListItemButton>

      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setJobExpanded(!jobExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <HomeRepairServiceIcon />
        </StyledListItemIcon>
        <ListItemText
          primary={workMenuLabel}
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {jobExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={jobExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/works/create")}
            disabled={company?.status != CompanyStatus.ACTIVE}
            selected={page == "works/create"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <CreateRounded fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={createNewWorkLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/calendar")}
            selected={page == "calendar"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <CalendarMonthOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={workCalendarLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/works")}
            selected={page == "works"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <ApprovalSharp fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={workManagementLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/locations")}
            selected={page == "locations"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <LocationOnIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={locationsLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/works/templates")}
            selected={page == "works/templates"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <ArticleOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={templatesLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>

      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setTalentExpanded(!talentExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <PeopleIcon />
        </StyledListItemIcon>
        <ListItemText
          primary={independentProfessionalsLabel}
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.95rem" },
            sx: {
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: { xs: 1.2, sm: 1.3 },
            },
          }}
        />
        {talentExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={talentExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/independent-professionals")}
            selected={page == "independent-professionals"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <ViewListOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={showListLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/independent-professionals/map")}
            selected={page == "independent-professionals/map"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <LocationOnIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={showMapLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/independent-professionals/favorites")}
            selected={page == "independent-professionals/favorites"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <FavoriteIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={favoritesLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>

      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setReportExpanded(!reportExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <AddchartOutlined />
        </StyledListItemIcon>
        <ListItemText
          primary={financesLabel}
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {reportExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={reportExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/finances")}
            selected={page.startsWith("finances")}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AttachMoneyIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={generalFinancesLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/reports/finance-report")}
            selected={page == "reports/finance-report"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <RequestQuoteOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={financialReportLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>

      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setSettingsExpanded(!settingsExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <SettingIcon />
        </StyledListItemIcon>
        <ListItemText
          primary={settingsLabel}
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {settingsExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/managers")}
            selected={page == "managers"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AddCircle fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={addManagersLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/settings/ui-wording")}
            selected={page == "settings/ui-wording"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AddCircle fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary={uiWordingLabel}
              primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" } }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>
    </List>
  );
};

export default SidebarMenu;
